import { Link } from 'react-router-dom';
import type { MhOrderRow } from '@/types/mh';
import { formatBalanceU } from '@/utils/product-display';
import {
  getOrderStatusStyle,
  isApiOrderWithResult,
  isP2pOrderWithResult,
  isP2pOrderWithContact,
} from '@/utils/order-display';
import { openSaleSmartlyChat } from '@/lib/salesmartly';

interface OrderCardProps {
  order: MhOrderRow;
}

function formatPayloadSummary(raw?: string): string {
  if (!raw) {
    return '—';
  }
  try {
    const o = JSON.parse(raw) as Record<string, string>;
    return Object.entries(o)
      .map(([k, v]) => `${k}: ${v}`)
      .join(' · ');
  } catch {
    return raw.length > 80 ? `${raw.slice(0, 80)}…` : raw;
  }
}

export default function OrderCard({ order }: OrderCardProps) {
  const st = getOrderStatusStyle(order.status);
  const showApiResult = isApiOrderWithResult(order);
  const showP2pResult = isP2pOrderWithResult(order);
  const showContact = isP2pOrderWithContact(order);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-gray-200 transition-all duration-200">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50 bg-gray-50/60">
        <div className="flex items-center space-x-2 text-xs text-gray-400">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-file-list-3-line" />
          </div>
          <span className="font-mono tracking-wide">{order.orderNo}</span>
        </div>
        <span
          className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${st.className}`}
        >
          <div className="w-3 h-3 flex items-center justify-center">
            <i className={st.icon} />
          </div>
          <span>{st.label}</span>
        </span>
      </div>

      <div className="p-5">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100">
            <i className="ri-search-eye-line text-xl text-gray-500" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900 leading-tight">{order.productName}</h3>
                <p className="text-xs text-gray-400 mt-0.5">信息查询订单</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-bold text-gray-900">{formatBalanceU(order.amount)}</p>
              </div>
            </div>

            <div className="mt-3 flex items-start space-x-1.5 text-xs text-gray-500">
              <div className="w-3.5 h-3.5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-user-search-line" />
              </div>
              <span className="break-all">提交信息：{formatPayloadSummary(order.paramPayload)}</span>
            </div>

            {order.status === 'FAILED' && order.auditRemark && (
              <div className="mt-2 flex items-start space-x-1.5 text-xs text-rose-600 bg-rose-50 rounded-lg px-3 py-2">
                <div className="w-3.5 h-3.5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-information-line" />
                </div>
                <span>失败说明：{order.auditRemark}</span>
              </div>
            )}

            <div className="mt-3 flex items-center space-x-1.5 text-xs text-gray-400">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-calendar-line" />
              </div>
              <span>下单时间：{order.createTime || '—'}</span>
            </div>
          </div>
        </div>
      </div>

      {(showApiResult || showP2pResult || showContact) && (
        <div className="px-5 pb-4 flex items-center justify-end space-x-2">
          {showApiResult && (
            <Link
              to={`/search/results?oid=${encodeURIComponent(order.orderNo)}`}
              className="px-4 py-2 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看结果
            </Link>
          )}
          {showP2pResult && (
            <Link
              to={`/orders/${encodeURIComponent(order.orderNo)}/results`}
              className="px-4 py-2 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看结果
            </Link>
          )}
          {showContact && (
            <button
              type="button"
              onClick={() =>
                openSaleSmartlyChat({
                  message: `订单咨询：${order.orderNo}\n产品：${order.productName}`,
                })
              }
              className="px-4 py-2 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              联系客服
            </button>
          )}
        </div>
      )}
    </div>
  );
}
